import {useEffect, useRef} from 'react'
import {useAppDispatch} from '../../common/store'
import {applyScenePreset} from '../../common/components/store/scenesSlice'
import {triggerCameraPreset} from '../../common/components/deviceMesh/cameraPresetStore'
import type {SceneFullConfig} from '../../common/components/types'
import {getProject} from '../../common/api/generated/endpoints/projects/projects'

/**
 * Shared hook that loads a project scene from the API and applies its config to Redux.
 * Used by FreeEditorRoute.
 */
const useProjectSceneLoader = (projectSlug: string | null, sceneIndex: number) => {
  const dispatch = useAppDispatch()
  const appliedRef = useRef(false)

  useEffect(() => {
    if (!projectSlug || appliedRef.current) return
    appliedRef.current = true

    const fetchAndApply = async () => {
      try {
        const {data} = await getProject(projectSlug)
        const targetScene = data.scenes[sceneIndex] ?? data.scenes[0]
        if (!targetScene) return

        // Strip screen images for user-authored projects (author's screenshots are private)
        if (data.author) {
          const cfg = targetScene.config as Record<string, unknown>
          delete cfg['textureKey']
          delete cfg['textureUrl']
        }

        dispatch(applyScenePreset(targetScene.config as unknown as SceneFullConfig))
        const camera = (targetScene.config as Record<string, unknown>).camera as
          | {position: number[]; rotation: number[]}
          | undefined
        if (camera) {
          triggerCameraPreset(camera.position, camera.rotation)
        }
      } catch {
        // Silently ignore — user gets default scene
      }
    }

    fetchAndApply()
  }, [projectSlug, sceneIndex, dispatch])
}

export default useProjectSceneLoader
